import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import { mergeAnalyticsPayloads } from '../utils/mergeAnalytics';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

/** Distinct series colors — primary family + semantic accents (charts only) */
const CHART_COLORS = [
  '#49B7F8',
  '#6bc9fa',
  '#3a9fd6',
  '#22c55e',
  '#f59e0b',
];

/**
 * UsageDashboard Component
 *
 * Displays comprehensive usage analytics for API keys including:
 * - Time-series graph (1d, 7d, 30d)
 * - Usage by chain breakdown
 * - Rate limit monitoring
 * - Daily/Monthly usage statistics
 */
/**
 * @param {object} [apiKey] - Single key: show detail for this key only.
 * @param {object[]} [apiKeys] - Multiple keys: fetch and merge into one "All keys" dashboard.
 */
const UsageDashboard = ({ apiKey, apiKeys }) => {
  const [period, setPeriod] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAggregate = Boolean(apiKeys?.length && !apiKey);

  const loadSingleAnalytics = useCallback(async () => {
    if (!apiKey?.id) return;

    try {
      setLoading(true);
      const data = await apiService.getApiKeyAnalytics(apiKey.uuid, period);
      setAnalytics(data);
    } catch (error) {
      // 404 here = the key was deleted while we still had it selected
      // (parent didn't switch to a fresh selection yet). Don't toast —
      // the parent will replace this card on the next refresh.
      const status = error?.status || error?.response?.status;
      if (status !== 404) {
        console.error('Error loading analytics:', error);
        toast.error('Failed to load usage analytics');
      }
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [apiKey?.id, period]);

  const loadAggregateAnalytics = useCallback(async () => {
    if (!apiKeys?.length) return;

    try {
      setLoading(true);
      // allSettled instead of all so a single failed key (deleted, race
      // with parent list refresh, etc.) doesn't kill the whole panel
      // and surface a misleading error toast.
      const settled = await Promise.allSettled(
        apiKeys.map((k) => apiService.getApiKeyAnalytics(k.uuid, period)),
      );
      const ok = settled
        .filter((r) => r.status === 'fulfilled' && r.value)
        .map((r) => r.value);
      if (ok.length > 0) {
        setAnalytics(mergeAnalyticsPayloads(ok));
      } else {
        toast.error('Failed to load usage analytics');
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Error loading aggregate analytics:', error);
      toast.error('Failed to load usage analytics');
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [apiKeys, period]);

  useEffect(() => {
    if (apiKey?.id) {
      loadSingleAnalytics();
      return;
    }
    if (apiKeys?.length) {
      loadAggregateAnalytics();
      return;
    }
    setLoading(false);
    setAnalytics(null);
  }, [
    apiKey?.id,
    apiKeys?.length,
    loadSingleAnalytics,
    loadAggregateAnalytics,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="font-secondary py-12 text-center text-white/65">
        No usage data available
      </div>
    );
  }

  // Calculate percentage usage
  const monthlyPercentage = analytics.limits.monthly_limit
    ? (analytics.limits.monthly_used / analytics.limits.monthly_limit) * 100
    : 0;

  const dailyPercentage = analytics.limits.daily_limit
    ? (analytics.limits.daily_used / analytics.limits.daily_limit) * 100
    : 0;

  // Overage state — surfaces the "OVERAGE" badge + per-million billing
  // rate when the org is over its monthly cap AND the plan allows overage.
  // Plans with overage_enabled=false hard-block at the cap (429).
  const monthlyOver = !!(
    analytics.limits.monthly_limit &&
    analytics.limits.monthly_used > analytics.limits.monthly_limit
  );
  const overAmount = monthlyOver
    ? analytics.limits.monthly_used - analytics.limits.monthly_limit
    : 0;
  const overageBilling = monthlyOver && analytics.limits.overage_enabled;

  // Format numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num;
  };

  return (
    <div className="space-y-6">
      {isAggregate && apiKeys?.length ? (
        <div className="rounded-sm border border-primary/25 bg-primary/5 px-4 py-3 sm:px-5">
          <p className="font-title text-sm font-semibold text-white sm:text-base">
            All API keys
          </p>
          <p className="font-secondary mt-1 text-xs text-white/60 sm:text-sm">
            Combined usage across {apiKeys.length} key
            {apiKeys.length !== 1 ? 's' : ''}. Select a key below to see per-key
            detail.
          </p>
        </div>
      ) : null}

      {/* Header with Period Selector */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-title text-2xl font-bold tracking-tight text-white">
            {isAggregate ? 'Usage overview' : 'Usage analytics'}
          </h3>
          <p className="font-secondary mt-1 text-sm text-white/65">
            {isAggregate
              ? 'Totals and charts merged for every key in this project'
              : 'Monitor your API usage and rate limits'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {['1d', '7d', '30d'].map((p) => (
            <motion.button
              type="button"
              key={p}
              onClick={() => setPeriod(p)}
              className={`font-secondary rounded-sm border px-4 py-2 text-sm font-semibold transition-colors ${
                period === p
                  ? 'border-primary bg-primary font-bold text-[#0a0a0a]'
                  : 'border-white/15 bg-transparent text-white/80 hover:bg-white/[0.06]'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {p === '1d' ? 'Today' : p === '7d' ? '7 days' : '30 days'}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Stats Cards — 4 columns from md; 2×2 on sm; stacked on xs */}
      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 md:gap-4">
        {/* Total Requests */}
        <motion.div
          className="flex h-full min-h-[148px] min-w-0 flex-col rounded-sm border border-primary/25 bg-primary/5 p-5 sm:p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-secondary text-sm font-medium text-white/65">
              Total requests
            </h4>
            <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div className="font-title text-3xl font-bold text-white">{formatNumber(analytics.total_requests)}</div>
          <div className="font-secondary mt-1 text-xs text-white/50">
            {formatNumber(analytics.daily_average)}/day avg
          </div>
        </motion.div>

        {/* Daily Usage */}
        <motion.div
          className="flex h-full min-h-[148px] min-w-0 flex-col rounded-sm border border-white/10 bg-[#0c0c0c] p-5 sm:p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-secondary text-sm font-medium text-white/65">
              Daily usage
            </h4>
            <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="font-title text-3xl font-bold text-white">{formatNumber(analytics.limits.daily_used)}</div>
          <div className="font-secondary mt-1 text-xs text-white/50">
            {analytics.limits.daily_limit ? (
              <>
                {formatNumber(analytics.limits.daily_remaining)} remaining
                <div className="mt-2 h-1 w-full rounded-full bg-white/10">
                  <div
                    className="h-1 rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${Math.min(dailyPercentage, 100)}%` }}
                  />
                </div>
              </>
            ) : (
              'Unlimited'
            )}
          </div>
        </motion.div>

        {/* Monthly Usage */}
        <motion.div
          className="flex h-full min-h-[148px] min-w-0 flex-col rounded-sm border border-white/10 bg-[#0c0c0c] p-5 sm:p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-secondary text-sm font-medium text-white/65">
              Monthly usage
            </h4>
            {overageBilling ? (
              <span className="rounded-sm border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 font-title text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                Overage
              </span>
            ) : monthlyOver ? (
              <span className="rounded-sm border border-red-400/40 bg-red-400/10 px-1.5 py-0.5 font-title text-[10px] font-semibold uppercase tracking-wider text-red-300">
                Cap exceeded
              </span>
            ) : (
              <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </div>
          <div className="font-title text-3xl font-bold text-white">{formatNumber(analytics.limits.monthly_used)}</div>
          <div className="font-secondary mt-1 text-xs text-white/50">
            {analytics.limits.monthly_limit ? (
              <>
                {overageBilling ? (
                  <span className="text-amber-300/90">
                    {formatNumber(overAmount)} over limit
                    {analytics.limits.usd_per_million_full != null ? (
                      <> · ${analytics.limits.usd_per_million_full}/M billed</>
                    ) : null}
                  </span>
                ) : monthlyOver ? (
                  <span className="text-red-300/90">Cap exceeded — requests blocked</span>
                ) : (
                  <>{formatNumber(analytics.limits.monthly_remaining)} remaining</>
                )}
                <div className="mt-2 h-1 w-full rounded-full bg-white/10">
                  <div
                    className={`h-1 rounded-full transition-all ${monthlyOver ? (overageBilling ? 'bg-amber-400' : 'bg-red-400') : 'bg-primary'}`}
                    style={{ width: `${Math.min(monthlyPercentage, 100)}%` }}
                  />
                </div>
              </>
            ) : (
              'Unlimited'
            )}
          </div>
        </motion.div>

        {/* Current Plan */}
        <motion.div
          className="flex h-full min-h-[148px] min-w-0 flex-col rounded-sm border border-white/10 bg-[#0c0c0c] p-5 sm:p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-secondary text-sm font-medium text-white/65">
              Current plan
            </h4>
            <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div className="font-title line-clamp-2 min-w-0 break-words text-2xl font-bold text-white">
            {analytics.current_plan.name}
          </div>
          <div className="font-secondary mt-1 text-xs text-white/50">
            {analytics.current_plan.rps} RPS
          </div>
        </motion.div>
      </div>

      {/* Bucket breakdown — RAW counts of each request class, with the
          formula that composes them into the rate-limit-enforced monthly
          counter. This is the user-facing answer to: "where is my monthly
          quota actually going?" Full + Archive + Special-chain combine
          PROPORTIONALLY into `monthly_used` via the plan's archive_weight
          and per-chain weight_override. Backend exposes raw counts in
          analytics.limits.{full_used, archive_used, special_used} sourced
          from Redis usage:bucket:* keys. */}
      {analytics.limits && (
        <BucketBreakdown
          limits={analytics.limits}
          formatNumber={formatNumber}
        />
      )}

      {/* Per-Chain Statistics Cards */}
      {analytics.chain_stats && analytics.chain_stats.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-title text-xl font-semibold tracking-tight text-white">
            Usage by chain
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {analytics.chain_stats.map((chainStat, index) => (
              <motion.div
                key={chainStat.chain}
                className="rounded-sm border border-white/10 bg-landing-section p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="h-4 w-4 rounded-full"
                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    />
                    <h4 className="font-title text-lg font-semibold text-white">{chainStat.chain}</h4>
                  </div>
                  <span className="font-secondary text-sm text-white/50">{chainStat.percentage}%</span>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="font-secondary mb-1 text-xs text-white/50">Total requests (last 30 days)</p>
                    <p className="font-title text-2xl font-bold text-white">{formatNumber(chainStat.total)}</p>
                  </div>
                  {/* Per-chain daily/monthly removed — those numbers came
                      from ApiKeyUsageDaily, which is api-key-wide (no chain
                      column), so every chain card showed identical
                      org/key-level totals. Misleading. The 30-day total
                      above is per-chain (from by_chain group) and accurate. */}

                  <div className="h-2 w-full rounded-full bg-white/10">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${chainStat.percentage}%`,
                        backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Time Series Chart - Takes 2 columns */}
        <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-4 sm:p-6 lg:col-span-2">
          <h4 className="font-title mb-4 text-lg font-semibold text-white">
            Requests over time by chain
          </h4>
          {analytics.by_date_chain && analytics.by_date_chain.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.by_date_chain}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis
                  dataKey="date"
                  stroke="rgba(255,255,255,0.35)"
                  tick={{ fill: 'rgba(255,255,255,0.55)' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis stroke="rgba(255,255,255,0.35)" tick={{ fill: 'rgba(255,255,255,0.55)' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1B1B1B',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '4px',
                    color: '#fff'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.65)' }} />
                {analytics.chain_stats && analytics.chain_stats.map((chainStat, index) => (
                  <Line
                    key={chainStat.chain}
                    type="monotone"
                    dataKey={chainStat.chain}
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS[index % CHART_COLORS.length], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={chainStat.chain}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[300px] items-center justify-center font-secondary text-white/50">
              No data available for this period
            </div>
          )}
        </div>

        {/* Chain Distribution - Takes 1 column */}
        <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-4 sm:p-6">
          <h4 className="font-title mb-4 text-lg font-semibold text-white">
            Usage by chain
          </h4>
          {analytics.by_chain && analytics.by_chain.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={analytics.by_chain}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#49B7F8"
                    dataKey="count"
                  >
                    {analytics.by_chain.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1B1B1B',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      color: '#fff'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {analytics.by_chain.map((chain, index) => (
                  <div key={index} className="font-secondary flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <span className="text-white/75">{chain.chain}</span>
                    </div>
                    <span className="font-medium text-white">{formatNumber(chain.count)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex h-[300px] items-center justify-center font-secondary text-white/50">
              No chain data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Bucket breakdown row: full / archive / special raw counts +
 * a formula line that explains how the three buckets compose into the
 * weighted `monthly_used` the gateway enforces. Cards stay visible even
 * with zero usage so the user can see what's tracked.
 */
const BucketBreakdown = ({ limits, formatNumber }) => {
  const fullUsed = Number(limits.full_used) || 0;
  const archiveUsed = Number(limits.archive_used) || 0;
  const archiveLimit = limits.archive_limit;

  const archivePct =
    archiveLimit && archiveLimit > 0
      ? Math.min(100, (archiveUsed / archiveLimit) * 100)
      : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-title text-xl font-semibold tracking-tight text-white">
          Request bucket breakdown
        </h3>
        <span className="font-secondary text-xs text-white/50">
          live counters
        </span>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4">
        {/* Full node — raw count of full-bucket requests */}
        <motion.div
          className="flex h-full min-h-[140px] min-w-0 flex-col rounded-sm border border-white/10 bg-[#0c0c0c] p-5"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h4 className="font-secondary mb-2 text-sm font-medium text-white/65">
            Full node requests
          </h4>
          <div className="font-title text-3xl font-bold text-white">
            {formatNumber(fullUsed)}
          </div>
          <div className="font-secondary mt-1 text-xs text-white/50">
            Standard RPC calls (most chains, common methods)
          </div>
        </motion.div>

        {/* Archive — raw count + cap from request_monthly_archive */}
        <motion.div
          className="flex h-full min-h-[140px] min-w-0 flex-col rounded-sm border border-white/10 bg-[#0c0c0c] p-5"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <h4 className="font-secondary mb-2 text-sm font-medium text-white/65">
            Archival requests
          </h4>
          <div className="font-title text-3xl font-bold text-white">
            {formatNumber(archiveUsed)}
            {archiveLimit ? (
              <span className="font-secondary text-sm font-medium text-white/40">
                {" "}
                / {formatNumber(archiveLimit)}
              </span>
            ) : null}
          </div>
          {archiveLimit ? (
            <div className="mt-2">
              <div className="h-1 w-full rounded-full bg-white/10">
                <div
                  className="h-1 rounded-full bg-[#7367f0] transition-all"
                  style={{ width: `${archivePct}%` }}
                />
              </div>
              <p className="font-secondary mt-1.5 text-xs text-white/50">
                Historical state (eth_getLogs, debug_*, archive-class chains)
              </p>
            </div>
          ) : (
            <p className="font-secondary mt-1 text-xs text-white/50">
              Historical state (eth_getLogs, debug_*, archive-class chains)
            </p>
          )}
        </motion.div>

      </div>
    </div>
  );
};

export default UsageDashboard;
